/**
 * iter315 — Module WhatsApp : consulter les conversations, surveiller le mode,
 * reprendre la main. Volontairement en lecture d'abord : tant que le mode est
 * « Désactivé », aucun message ne peut partir, et l'écran le dit clairement.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MessageCircle, ShieldAlert, RefreshCw, AlertTriangle, Settings2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WhatsAppConversation from "@/components/WhatsAppConversation";

const MODES = {
  desactive: { label: "Désactivé", aide: "Réception et historique seulement. Aucun envoi, Claude n'est pas appelé.", couleur: "bg-gray-100 text-gray-700 border-gray-300" },
  test: { label: "Test", aide: "Claude rédige, les réponses restent dans le CRM. Rien ne part, sauf vers les numéros autorisés.", couleur: "bg-amber-100 text-amber-800 border-amber-300" },
  auto: { label: "Automatique", aide: "Claude répond aux clients sur WhatsApp. À n'activer qu'en connaissance de cause.", couleur: "bg-[#52a675]/15 text-[#3d8a5c] border-[#52a675]/40" },
};

export default function WhatsApp() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [statut, setStatut] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [actif, setActif] = useState(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/whatsapp/status"),
        api.get("/whatsapp/conversations", { params: { limite: 100 } }),
      ]);
      setStatut(s.data);
      setConversations(c.data.items || []);
    } catch {
      toast.error("Impossible de charger le module WhatsApp");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const changerMode = async (mode) => {
    try {
      await api.post("/whatsapp/mode", { mode });
      toast.success(`Mode « ${MODES[mode].label} »`, { description: MODES[mode].aide });
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Changement de mode refusé");
    }
  };

  const simuler = async () => {
    const wa_id = window.prompt("Numéro WhatsApp à simuler (ex. 41791112233)");
    if (!wa_id) return;
    const texte = window.prompt("Message du client");
    if (!texte) return;
    try {
      await api.post("/whatsapp/simuler", { wa_id, texte });
      toast.success("Message injecté", { description: "Traitement en cours, rechargez dans un instant." });
      setTimeout(charger, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation impossible");
    }
  };

  const mode = statut?.reglages?.mode || "desactive";
  const file = statut?.file || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="whatsapp-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-[#52a675]" /> WhatsApp
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {statut?.conversations ?? 0} conversation(s) · {statut?.evenements ?? 0} événement(s) reçu(s)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={charger} data-testid="whatsapp-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={simuler} data-testid="whatsapp-simuler">
              <FlaskConical className="w-4 h-4 mr-2" /> Simuler un message
            </Button>
          )}
        </div>
      </div>

      <Card className={`p-4 mb-6 border ${MODES[mode].couleur}`} data-testid="whatsapp-mode-card">
        <div className="flex items-start gap-3 flex-wrap">
          <Settings2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1 basis-full sm:basis-auto">
            <div className="font-semibold">Mode : {MODES[mode].label}</div>
            <p className="text-xs mt-0.5 opacity-90">{MODES[mode].aide}</p>
            {statut?.reglages?.maj_par && (
              <p className="text-xs mt-1 opacity-70">
                Dernier changement par {statut.reglages.maj_par}
              </p>
            )}
          </div>
          {isAdmin && (
            <Select value={mode} onValueChange={changerMode}>
              <SelectTrigger className="w-full sm:w-48 bg-white" data-testid="whatsapp-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MODES).map(([cle, m]) => (
                  <SelectItem key={cle} value={cle} data-testid={`whatsapp-mode-${cle}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {statut?.secrets?.WHATSAPP_ACCESS_TOKEN === "absent" && (
          <div className="mt-3 flex gap-2 text-xs bg-white/70 rounded p-2" data-testid="whatsapp-alerte-token">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              Jeton d'accès Meta absent du serveur : même en mode automatique, aucun message
              ne peut techniquement partir.
            </span>
          </div>
        )}
        {(file.abandonne > 0 || file.en_attente > 0) && (
          <p className="mt-2 text-xs opacity-80" data-testid="whatsapp-file">
            File : {file.en_attente || 0} en attente · {file.abandonne || 0} abandonné(s)
          </p>
        )}
      </Card>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <Card className="border-gray-200 overflow-hidden" data-testid="whatsapp-liste">
          <div className="px-4 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-700">
            Conversations
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y">
            {chargement && <div className="p-6 text-center text-sm text-gray-400">Chargement…</div>}
            {!chargement && conversations.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-400">
                Aucune conversation pour l'instant
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.wa_id}
                onClick={() => setActif(c.wa_id)}
                className={`w-full text-left px-4 py-3 hover:bg-[#f0f9f4] transition-colors ${
                  actif === c.wa_id ? "bg-[#f0f9f4] border-l-2 border-l-[#52a675]" : ""
                }`}
                data-testid={`whatsapp-conv-${c.wa_id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">
                    {c.client_label || c.nom_contact || c.wa_id}
                  </span>
                  {c.non_lus > 0 && (
                    <Badge className="bg-[#52a675] hover:bg-[#52a675] text-white">{c.non_lus}</Badge>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>+{c.wa_id}</span>
                  {!c.client_id && <span className="text-amber-600">non rattaché</span>}
                  {c.identite_verifiee
                    ? <span className="text-[#3d8a5c]">identité vérifiée</span>
                    : <span className="inline-flex items-center gap-1 text-gray-400">
                        <ShieldAlert className="w-3 h-3" /> non vérifiée
                      </span>}
                  {c.reprise_humaine && <span className="text-blue-600">repris</span>}
                  {c.escalade && <span className="text-red-600">à traiter</span>}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {actif ? (
          <WhatsAppConversation waId={actif} onChange={charger} />
        ) : (
          <Card className="border-gray-200 p-10 text-center text-sm text-gray-400" data-testid="whatsapp-vide">
            Sélectionnez une conversation pour voir les échanges.
          </Card>
        )}
      </div>
    </div>
  );
}
